import React, {useEffect} from 'react'
import styled from '@emotion/styled'
import {ContentHotel} from '@findhotel/sapi/dist/types/packages/core/src'
import {FacilitiesGrid} from 'components/Accommodation/Facilities'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {OverlayType} from 'modules/overlay/types'
import {getHotelFacilitiesRanked, TOP_FACILITY_IDS} from 'utils/facilities'

import {ButtonLink} from '@vio/atlas/ButtonLink'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {MAXIMUM_POPULAR_FACILITIES} from './constants'
import {PopularFacilitiesLoading} from './PopularFacilitiesLoading'

import type {Columns} from 'components/Accommodation/Facilities/FacilitiesGrid'

interface Props {
  hotel: ContentHotel
  columns?: Columns
  hideTitle?: boolean
  gap?: 's300' | 's500'
  facilitiesRowGap?: 's300' | 's500'
}

const ShowMoreButton = styled(ButtonLink)`
  width: fit-content;
`

export const PopularFacilities = ({
  hotel,
  columns,
  hideTitle,
  gap = 's500',
  facilitiesRowGap
}: Props) => {
  const showOverlay = useShowOverlay()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const {isMobile} = useDeviceLayout()

  const {ref, inView} = useInView({triggerOnce: true, threshold: 0.5})

  const onSeeMore = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.HotelDetailsSeeMoreFacilities,
        action: Action.Clicked
      })
    )
    showOverlay({
      type: OverlayType.HotelFacilities,
      params: {
        hotelId: hotel.objectID
      }
    })
  }

  useEffect(() => {
    if (inView) {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.HotelDetailsFacilitiesSection,
          action: Action.Viewed
        })
      )
    }
  }, [dispatchTeamEvent, inView])

  if (!hotel?.facilities) return <PopularFacilitiesLoading />

  const topFacilities = getHotelFacilitiesRanked(
    hotel.facilities,
    TOP_FACILITY_IDS,
    MAXIMUM_POPULAR_FACILITIES
  )

  const hasMore = hotel.facilities.length > MAXIMUM_POPULAR_FACILITIES

  return (
    <Box display="flex" flexDirection="column" gap={gap}>
      {!hideTitle && (
        <Text variant="titleL" tag="h2">
          <FormattedMessageWrapper id="amenities" defaultMessage="Amenities" />
        </Text>
      )}
      <div ref={ref} data-id="amenities-section">
        <FacilitiesGrid
          facilities={topFacilities}
          columns={columns}
          rowGap={facilitiesRowGap}
        />
      </div>
      {hasMore && (
        <ShowMoreButton
          onClick={onSeeMore}
          size={isMobile ? 'md' : 'lg'}
          variant="primary"
          isCompact
        >
          <FormattedMessageWrapper id="showAll" defaultMessage="Show all" />
        </ShowMoreButton>
      )}
    </Box>
  )
}
